import { SportCompetitor } from "@fresh8/types/src/betData";
import { SportEnum } from "@fresh8/types/src/language";
import { injectEvent } from "@fresh8/ad-data-composition/src/betData";
import { computed } from "vue";

type StatsKeys =
  | SportEnum.FOOTBALL
  | SportEnum.BASEBALL
  | SportEnum.AMERICAN_FOOTBALL;

const statisticsKeyMap: Record<StatsKeys, PropertyKey> = {
  [SportEnum.FOOTBALL]: "statistics",
  [SportEnum.BASEBALL]: "statistics_baseball",
  [SportEnum.AMERICAN_FOOTBALL]: "statistics_american_football"
};

export const FORMAT_KEYS = {
  LEADING: "leading-decimal", // eg .123
  DECIMAL_ONE: "decimal-one", // eg 2.4,
  DECIMAL_TWO: "decimal-two", // eg 2.44,
  WHOLE: "whole" // eg 2
} as const;

type ValueOf<T> = T[keyof T];
export type H2hFormatKeys = ValueOf<typeof FORMAT_KEYS>;

export interface H2HFormatedData {
  text: string;
  left: number;
  right: number;
  reversed: boolean;
  format: H2hFormatKeys;
}

// array of strings where first element is always undefined
type HeadingsArray = [undefined, ...string[]];

const reverseCalcMap = {
  [SportEnum.FOOTBALL]: ["goals_conceded"],
  [SportEnum.BASEBALL]: [
    "runs_allowed_per_game",
    "hits_allowed_per_game",
    "batting_average_against",
    "homeruns_allowed_per_game"
  ],
  [SportEnum.AMERICAN_FOOTBALL]: ["points_allowed_per_game"]
};

const formatMap = {
  [SportEnum.FOOTBALL]: {
    // statistics
    goals_scored: FORMAT_KEYS.DECIMAL_ONE,
    shots: FORMAT_KEYS.DECIMAL_ONE,
    shots_on_target: FORMAT_KEYS.DECIMAL_ONE,
    ball_possession: FORMAT_KEYS.DECIMAL_ONE,
    goals_conceded: FORMAT_KEYS.DECIMAL_ONE
  },
  [SportEnum.BASEBALL]: {
    // general_stats
    runs_per_game: FORMAT_KEYS.DECIMAL_ONE,
    runs_allowed_per_game: FORMAT_KEYS.DECIMAL_ONE,
    hits_per_game: FORMAT_KEYS.DECIMAL_ONE,
    hits_allowed_per_game: FORMAT_KEYS.DECIMAL_ONE,
    // batting_stats
    batting_average: FORMAT_KEYS.LEADING,
    on_base_percentage: FORMAT_KEYS.LEADING,
    slugging_percentage: FORMAT_KEYS.LEADING,
    home_runs: FORMAT_KEYS.WHOLE,
    stolen_bases: FORMAT_KEYS.WHOLE,
    // pitching stats
    earned_run_average: FORMAT_KEYS.DECIMAL_TWO,
    walks_hits_per_inning_pitched: FORMAT_KEYS.DECIMAL_TWO,
    strikeouts_per_9_innings: FORMAT_KEYS.DECIMAL_TWO,
    homeruns_allowed_per_game: FORMAT_KEYS.DECIMAL_TWO,
    batting_average_against: FORMAT_KEYS.LEADING
  },
  [SportEnum.AMERICAN_FOOTBALL]: {
    // general_stats
    points_per_game: FORMAT_KEYS.DECIMAL_ONE,
    points_allowed_per_game: FORMAT_KEYS.DECIMAL_ONE,
    // rushing_stats
    rushing_yards_per_game: FORMAT_KEYS.WHOLE,
    rushing_touchdowns: FORMAT_KEYS.WHOLE,
    rushing_percentage: FORMAT_KEYS.DECIMAL_ONE,
    // passing_stats
    passing_yards_per_game: FORMAT_KEYS.WHOLE,
    passing_touchdowns: FORMAT_KEYS.WHOLE,
    passing_percentage: FORMAT_KEYS.DECIMAL_ONE
  }
};

function recordReducer(acc, [key, value]) {
  acc[key.charAt(0).toUpperCase()] = value;
  return acc;
}

export function useH2HStatistics(competitors: SportCompetitor[]) {
  const event = injectEvent();
  const sport = computed(() => event.value.sport);
  const key = computed(() => statisticsKeyMap[sport.value]);

  const leftStats = computed(() => competitors[0][key.value]);
  const rightStats = computed(() => competitors[1][key.value]);

  const meta = computed(() => {
    const leftRecord: Record<"W" | "L", string> = leftStats.value?.record
      ? Object.entries(leftStats.value?.record).reduce(
          recordReducer,
          {} as Record<"W" | "L", string>
        )
      : {};
    const rightRecord: Record<"W" | "L", string> = leftStats.value?.record
      ? Object.entries(rightStats.value?.record).reduce(
          recordReducer,
          {} as Record<"W" | "L", string>
        )
      : {};

    const leftForm = competitors[0].form as SportCompetitor["form"];
    const rightForm = competitors[1].form as SportCompetitor["form"];

    return {
      record:
        leftRecord.hasOwnProperty("W") && rightRecord.hasOwnProperty("W")
          ? [leftRecord, rightRecord]
          : [],
      form: leftForm?.length && rightForm?.length ? [leftForm, rightForm] : []
    };
  });

  const headings = computed<HeadingsArray>(() => {
    if (key.value === "statistics") {
      return [undefined];
    }

    const leftHeadingKeys = Object.keys(leftStats.value).filter(e => {
      return e !== "record" && e !== "general_stats";
    });

    const rightHeadingKeys = Object.keys(rightStats.value).filter(e => {
      return e !== "record" && e !== "general_stats";
    });

    const keys = leftHeadingKeys.filter(key => rightHeadingKeys.includes(key));

    return [undefined, ...keys];
  });

  const data = computed(() => {
    if (key.value === "statistics") {
      return [
        Object.entries(leftStats.value).map(([k, v]) => {
          return {
            text: k,
            left: v as number,
            right: rightStats.value[k] as number,
            reversed: reverseCalcMap[sport.value].includes(k),
            format: formatMap[sport.value][k]
          } as H2HFormatedData;
        })
      ];
    }

    function mapStats(key: string) {
      return Object.entries(leftStats.value[key]).map(([k, v]) => {
        return {
          text: k,
          left: v || 0,
          right: rightStats.value[key][k] || 0,
          reversed: reverseCalcMap[sport.value].includes(k),
          format: formatMap[sport.value][k]
        } as H2HFormatedData;
      });
    }

    const result = headings.value.map(e => {
      if (e === undefined) {
        return mapStats("general_stats");
      }

      return mapStats(e);
    });

    return result;
  });

  return {
    meta,
    headings,
    data
  };
}
